<template>
<div class="allocation-detail">
    <allocation
        :allocation="savedAllocation"
        :projects="projects"
        :week="week"
        :week2="week2"
        :broken="broken"
        :broken2="broken2"
        :show-projects="showProjects"
        :show-header="false"
        @changed="onChanged"
    />
    <div class="centered m-top-10 m-bottom-20">
        <button type="button" class="btn btn-sm"
                :class="isSaved ? 'btn-success' : 'btn-primary'"
                :disabled="!state.dirty || !state.valid || state.saving" @click="onSave">
            {{ state.saving ? 'Saving…' : (state.dirty ? 'Save allocation' : 'Saved') }}
        </button>
        <span v-if="!state.valid" class="text-danger m-left-10">
            Split must total 100% and match total pay &amp; allowances before saving.
        </span>
        <span v-else-if="state.savedOk" class="text-success m-left-10">✓ Saved</span>
        <span v-if="state.error" class="text-danger m-left-10">{{ state.error }}</span>
    </div>
</div>
</template>
<script>
import { defineComponent } from 'vue'
import Allocation from './allocation.vue'

// Detail-row renderer for the bulk allocation grid. It reuses the single-timecard
// <allocation> component verbatim so the split / redistribute behavior matches the
// per-timecard page exactly.
//
// IMPORTANT: ag-grid-vue3 v25 mounts a cell/detail renderer as its OWN Vue app and
// REPLACES the component's data() with { params, parent } (see
// node_modules/ag-grid-vue3/lib/VueComponentFactory.js). So this component keeps NO
// data() of its own — every piece of mutable UI state lives on the master-grid app
// (ctx.uiState[rowId], which is reactive across app instances) and is read through
// `state`. ag-grid passes the master row as params.data and our app as params.ctx.
export default defineComponent({
    name: 'AllocationDetail',

    components: { Allocation },

    // Mounted manually by the grid's standard detail renderer (see timecardAllocate.js),
    // which does createApp(AllocationDetail, { params }).mount(div). `params` is ag-grid's
    // detail params: the master row in params.data, and our app in params.ctx (from
    // detailCellRendererParams). All mutable UI state lives on the master app
    // (ctx.uiState[rowId], reactive across app instances) and is read through `state`.
    props: {
        params: { type: Object, required: true },
    },

    computed: {
        ctx()   { return (this.params && this.params.ctx) || {}; },
        row()   { return (this.params && this.params.data) || {}; },
        state() { return this.ctx.rowState ? this.ctx.rowState(this.row.id) : {}; },

        projects()     { return this.ctx.projects || []; },
        showProjects() { return this.ctx.showProjects !== false; },

        // <allocation> reads week.day_N.project_id and broken.* — both come straight off
        // the timecard row (decoded defensively: the field may be a JSON string or object).
        week()    { return this.decode(this.row.timecard_object)  || {}; },
        week2()   { return this.decode(this.row.timecard_object2) || {}; },
        broken()  { return this.decode(this.row.broken_object)    || {}; },
        broken2() { return this.decode(this.row.broken_object2)   || {}; },

        savedAllocation() {
            const o = this.decode(this.row.overrides) || {};
            return (o && Array.isArray(o.allocation)) ? o.allocation : [];
        },

        // The button reads "Saved" (and turns green) when the split matches what's persisted.
        isSaved() {
            return !this.state.saving && !this.state.dirty;
        },
    },

    methods: {
        decode(v) {
            if (v == null) return null;
            if (typeof v === 'object') return v;
            if (typeof v === 'string') { try { return JSON.parse(v); } catch (e) { return null; } }
            return null;
        },
        // Fired by <allocation> on mount (default split) and on every edit.
        onChanged(payload, isValid) {
            if (this.ctx.onRowAllocationChanged) this.ctx.onRowAllocationChanged(this.row, payload, isValid);
        },
        onSave() {
            // advance = true: on success, collapse this row and open the next unallocated one.
            if (this.ctx.saveRow) this.ctx.saveRow(this.row, true);
        },
    },
});
</script>
<style scoped>
.allocation-detail {
    padding: 10px 20px;
    background: #fafafa;
}
</style>
